/**
 * Pure-function logic for Onboarding permission resolution.
 * Mirrors the My vs All scope pattern used by To-Do and Checklist modules.
 */

import { OnboardingRecord } from 'core/API/onboarding';
import {
	SecurityRolePermission,
	hasModuleViewPermission,
	hasModuleEditPermission,
	hasModuleDeletePermission,
	hasModuleExecutePermission,
} from 'tests/services/permissionService';

export const ONBOARDING_MY_LABEL = 'Onboarding - My';
export const ONBOARDING_ALL_LABEL = 'Onboarding - All';

export interface OnboardingPermissions {
	canView: boolean;
	canViewAll: boolean;
	canEdit: boolean;
	canDelete: boolean;
	canExecute: boolean;
}

/**
 * Derives the module-level Onboarding permission set from a SecurityRolePermission array.
 */
export function resolveOnboardingPermissions(
	perms: SecurityRolePermission[],
): OnboardingPermissions {
	const hasAll = hasModuleViewPermission(perms, ONBOARDING_ALL_LABEL);
	const hasMy = hasModuleViewPermission(perms, ONBOARDING_MY_LABEL);

	let canEdit = false;
	let canDelete = false;

	if (hasAll) {
		canEdit =
			hasModuleEditPermission(perms, ONBOARDING_ALL_LABEL) ||
			hasModuleEditPermission(perms, ONBOARDING_MY_LABEL);
		canDelete =
			hasModuleDeletePermission(perms, ONBOARDING_ALL_LABEL) ||
			hasModuleDeletePermission(perms, ONBOARDING_MY_LABEL);
	} else if (hasMy) {
		canEdit = hasModuleEditPermission(perms, ONBOARDING_MY_LABEL);
		canDelete = hasModuleDeletePermission(perms, ONBOARDING_MY_LABEL);
	}

	const canExecute =
		hasModuleExecutePermission(perms, ONBOARDING_ALL_LABEL) ||
		hasModuleExecutePermission(perms, ONBOARDING_MY_LABEL);

	return {
		canView: hasAll || hasMy,
		canViewAll: hasAll,
		canEdit,
		canDelete,
		canExecute,
	};
}

export function canViewOnboardingModule(perms: SecurityRolePermission[]): boolean {
	return resolveOnboardingPermissions(perms).canView;
}

export function getCurrentUserId(): number | null {
	try {
		const app =
			typeof window !== 'undefined' && localStorage.getItem('BYOD-app')
				? JSON.parse(localStorage.getItem('BYOD-app') as string)
				: null;
		const idFromApp =
			app && app.user && (app.user.id || app.user['@id']) ? app.user.id || app.user['@id'] : null;
		if (idFromApp) return Number(idFromApp);
		const idFromLS = localStorage.getItem('userId');
		if (idFromLS) return Number(idFromLS);
	} catch {
		// ignore
	}
	return null;
}

export function extractUserIdFromReference(ref: unknown): number | null {
	if (!ref) return null;
	if (typeof ref === 'object') {
		const obj = ref as { id?: number | string; '@id'?: string };
		if (obj['@id']) {
			const match = String(obj['@id']).match(/(\d+)$/);
			if (match) return Number(match[1]);
		}
		if (obj.id !== undefined && obj.id !== null) {
			const parsed = Number(obj.id);
			return Number.isNaN(parsed) ? null : parsed;
		}
	}
	if (typeof ref === 'string') {
		const match = ref.match(/(\d+)$/);
		if (match) return Number(match[1]);
	}
	return null;
}

export function isOnboardingOwnedByUser(
	record: OnboardingRecord,
	userId: number | null,
): boolean {
	if (!userId) return false;
	const creatorId =
		extractUserIdFromReference(record.initiatedBy) ??
		extractUserIdFromReference(record.createdBy);
	return creatorId === userId;
}

export function canViewOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	if (!permissions.canView) return false;
	if (permissions.canViewAll) return true;
	return isOnboardingOwnedByUser(record, currentUserId);
}

export function canEditOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	return canViewOnboardingRecord(permissions, record, currentUserId) && permissions.canEdit;
}

export function canDeleteOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	return canViewOnboardingRecord(permissions, record, currentUserId) && permissions.canDelete;
}

export function canExecuteOnboardingRecord(
	permissions: OnboardingPermissions,
	record: OnboardingRecord,
	currentUserId: number | null,
): boolean {
	return canViewOnboardingRecord(permissions, record, currentUserId) && permissions.canExecute;
}

export function canStartOnboarding(permissions: OnboardingPermissions): boolean {
	return permissions.canEdit;
}

export function filterVisibleOnboardingRecords(
	records: OnboardingRecord[],
	permissions: OnboardingPermissions,
	currentUserId: number | null,
): OnboardingRecord[] {
	if (permissions.canViewAll) return records;
	return records.filter((record) => canViewOnboardingRecord(permissions, record, currentUserId));
}
